import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MapPin, Clock, Star, Menu, X, Facebook, Instagram, ChevronDown, ChevronLeft, ChevronRight, Scissors, Quote, ArrowRight, Users, Award, Heart, Save, Trash2, ImagePlus, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_MAPS_EMBED_URL = "https://www.google.com/maps?output=embed&q=Gent%27s%20Classic%20Barber%20Shop%2C%20N88W16758%20Main%20St%2C%20Menomonee%20Falls%2C%20WI%2053051";
const GOOGLE_MAPS_REVIEWS_URL = "https://maps.app.goo.gl/HsmSpZBmyuMisVH38";

// Assets
const LOGO_URL = "/images/logo-primary.png";
const TEAM_PHOTO_URL = "/images/team-hero.jpg";
const SHOP_EXTERIOR_URL = "/images/shop-exterior-front.jpg";
const SHOP_INTERIOR_URL = "/images/shop-interior-main.jpg";

// Background images for pages
const BG_BARBER_WORK = "/images/barber-work-background.jpg";
const BG_BEARD = "/images/services-beard-background.jpg";
const BG_GALLERY = "/images/shop-gallery-background.jpg";
const BG_SHOP = "/images/shop-gallery-background.jpg";
const BG_HERO = "/images/hero-background.jpg";

// Business Data
const BUSINESS_INFO = {
  name: "Gent's Classic Barber Shop",
  tagline: "Where Tradition Meets Craft",
  phone: "(262) 293-9841",
  phoneClean: "+12622939841",
  address: "N88W16758 Main St",
  city: "Menomonee Falls, WI 53051",
  rating: 4.7,
  reviewCount: 159,
  website: "gentsbarber.shop",
  description: "Premium grooming experience in Menomonee Falls, Wisconsin",
  hours: [
  { day: "Monday", time: "Closed", closed: true },
  { day: "Tuesday", time: "10:00 AM – 6:00 PM" },
  { day: "Wednesday", time: "10:00 AM – 6:00 PM" },
  { day: "Thursday", time: "10:00 AM – 6:00 PM" },
  { day: "Friday", time: "9:00 AM – 5:00 PM" },
  { day: "Saturday", time: "8:00 AM – 2:00 PM" },
  { day: "Sunday", time: "Closed", closed: true }]

};

const DEFAULT_SERVICES = [
{ id: "service-haircut", name: "Haircut", price: "$30", description: "Classic cut tailored to your style and face shape", duration: "30 min", popular: true },
{ id: "service-haircut-beard", name: "Haircut & Beard", price: "$40", description: "The full package - haircut plus beard sculpting", duration: "45 min", popular: true },
{ id: "service-beard-trim", name: "Beard Trim", price: "$20", description: "Precision beard shaping and maintenance", duration: "20 min", popular: false },
{ id: "service-bald-fade", name: "Bald Fade", price: "$35", description: "The renowned bald fade - clean and sharp", duration: "35 min", popular: true },
{ id: "service-bald-fade-beard", name: "Bald Fade & Beard", price: "$45", description: "Bald fade combined with expert beard work", duration: "50 min", popular: false },
{ id: "service-military", name: "Military", price: "$24", description: "Regulation cut for our service members", duration: "25 min", popular: false },
{ id: "service-over-60", name: "Over 60", price: "$24", description: "Senior discount for our distinguished gentlemen", duration: "30 min", popular: false },
{ id: "service-shave", name: "Shave", price: "$40", description: "Old-school straight razor shave with hot towel", duration: "40 min", popular: false },
{ id: "service-shave-haircut", name: "Shave & Haircut", price: "$60", description: "Complete grooming experience - the works", duration: "60 min", popular: false },
{ id: "service-shampoo", name: "Shampoos", price: "$6", description: "Refreshing hair wash treatment", duration: "10 min", popular: false }];


const BARBERS = [
{ name: "Will", role: "Master Barber", bio: "With years of experience, Will brings precision and artistry to every cut. Known for his attention to detail and friendly conversation.", specialty: "Fades & Classic Cuts" },
{ name: "Nick", role: "Senior Barber", bio: "Nick combines modern techniques with traditional barbering skills. His clients appreciate his consistent quality and easy-going nature.", specialty: "Beard Work & Hot Shaves" }];


const TESTIMONIALS = [
{ name: "Marcus T.", text: "Best fade in Menomonee Falls, hands down. The attention to detail is incredible. Been coming here for years and never disappointed.", rating: 5, date: "March 2026", service: "Bald Fade" },
{ name: "James R.", text: "Took my son for his first real haircut here. The barber was patient, skilled, and made the experience comfortable. We'll be regulars for sure.", rating: 5, date: "February 2026", service: "Haircut" },
{ name: "David K.", text: "Old-school barbershop experience with modern skill. Hot towel shave was absolutely luxurious. This is what a barbershop should feel like.", rating: 5, date: "January 2026", service: "Shave" },
{ name: "Michael S.", text: "Found this gem a few months ago and haven't looked back. Will really knows his stuff. Great atmosphere, great people, great cuts.", rating: 5, date: "December 2025", service: "Haircut & Beard" },
{ name: "Robert H.", text: "The warm atmosphere makes every visit special. Feels like going back in time to when barbershops were community hubs.", rating: 5, date: "November 2025", service: "Military Cut" },
{ name: "Tom W.", text: "Nick gave me the best beard trim I've ever had. These guys are true craftsmen. Highly recommend!", rating: 5, date: "October 2025", service: "Beard Trim" }];


const DEFAULT_GALLERY_IMAGES = [
{ id: "gallery-local-1", url: TEAM_PHOTO_URL, category: "Team" },
{ id: "gallery-local-2", url: SHOP_EXTERIOR_URL, category: "Shop" },
{ id: "gallery-local-3", url: SHOP_INTERIOR_URL, category: "Shop" },
{ id: "gallery-local-4", url: "/images/haircut-gallery-01.webp", category: "Haircuts" },
{ id: "gallery-local-5", url: "/images/haircut-gallery-02.webp", category: "Haircuts" },
{ id: "gallery-local-6", url: "/images/haircut-gallery-03.webp", category: "Haircuts" },
{ id: "gallery-local-7", url: "/images/haircut-gallery-04.webp", category: "Haircuts" },
{ id: "gallery-local-8", url: "/images/team-gallery-01.webp", category: "Team" },
{ id: "gallery-local-9", url: "/images/team-gallery-02.webp", category: "Team" },
{ id: "gallery-local-10", url: "/images/team-gallery-03.webp", category: "Team" },
{ id: "gallery-local-11", url: "/images/team-gallery-04.webp", category: "Team" },
{ id: "gallery-local-12", url: "/images/shop-gallery-01.webp", category: "Shop" },
{ id: "gallery-local-13", url: "/images/haircut-gallery-05.webp", category: "Haircuts" },
{ id: "gallery-local-14", url: "/images/haircut-gallery-06.webp", category: "Haircuts" },
{ id: "gallery-local-15", url: "/images/haircut-gallery-07.webp", category: "Haircuts" },
{ id: "gallery-local-16", url: "/images/haircut-gallery-08.webp", category: "Haircuts" },
{ id: "gallery-local-17", url: "/images/haircut-gallery-09.webp", category: "Haircuts" },
{ id: "gallery-local-18", url: "/images/haircut-gallery-10.webp", category: "Haircuts" },
{ id: "gallery-local-19", url: "/images/shop-gallery-02.webp", category: "Shop" }];


const FEATURES = [
{ icon: Scissors, title: "Expert Cuts", desc: "Traditional techniques meet modern style" },
{ icon: Users, title: "Skilled Barbers", desc: "Will & Nick - masters of their craft" },
{ icon: Heart, title: "Warm Atmosphere", desc: "Old-world charm, community feel" },
{ icon: Award, title: "4.7★ Rated", desc: "159+ happy customers" }];


// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const pageTransition = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } }
};

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" }];


  return (
    <nav
      data-testid="navbar"
      className={`ref-header ${isScrolled ? "ref-header-scrolled" : ""}`}>

      <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
        <Link to="/" className="flex items-center gap-4" data-testid="nav-logo">
          <div className="ref-nav-logo-plate">
            <img src={LOGO_URL} alt="Gent's Classic" className="logo-mark logo-mark-nav" />
          </div>
          <div className="hidden sm:block ref-brand-lockup">
            <span className="ref-brand-title">Gent's Classic</span>
            <span className="ref-brand-subtitle">Barber Shop · Menomonee Falls</span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
          <Link
            key={link.to}
            to={link.to}
            className={`ref-nav-link ${
            location.pathname === link.to ? "ref-nav-link-active" : ""}`
            }
            data-testid={`nav-link-${link.label.toLowerCase()}`}>

              {link.label.toUpperCase()}
            </Link>
          )}
          <a href={`tel:${BUSINESS_INFO.phoneClean}`} data-testid="nav-call-btn">
            <button className="ref-nav-book flex items-center gap-2 text-sm">
              <Phone size={16} />
              CALL NOW
            </button>
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="lg:hidden ref-mobile-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          data-testid="mobile-menu-btn">

          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen &&
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="lg:hidden absolute top-full left-0 right-0 ref-mobile-menu overflow-hidden"
          data-testid="mobile-menu">

            <div className="ref-mobile-links flex flex-col p-6 gap-4">
              {navLinks.map((link) =>
            <Link
              key={link.to}
              to={link.to}
              className={`ref-mobile-link ${
              location.pathname === link.to ? "ref-mobile-link-active" : ""}`
              }
              onClick={() => setIsMobileMenuOpen(false)}>

                  {link.label.toUpperCase()}
                </Link>
            )}
              <a href={`tel:${BUSINESS_INFO.phoneClean}`} className="mt-4">
                <button className="ref-nav-book w-full flex items-center justify-center gap-2">
                  <Phone size={16} />
                  {BUSINESS_INFO.phone}
                </button>
              </a>
            </div>
          </motion.div>
        }
      </AnimatePresence>
    </nav>);

};

// Footer Component
const Footer = () =>
<footer className="py-16 bg-[#111] border-t border-[#d4af37]/10" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid md:grid-cols-4 gap-12 mb-12">
        {/* Brand */}
        <div className="md:col-span-2">
          <div className="flex items-center gap-4 mb-4">
            <div className="logo-shell logo-shell-footer">
              <img src={LOGO_URL} alt="Logo" className="logo-mark logo-mark-footer" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white" style={{ fontFamily: 'Georgia, serif' }}>GENT'S CLASSIC</h3>
              <span className="text-xs tracking-[4px] text-[#d4af37]" style={{ fontFamily: 'Georgia, serif' }}>BARBER SHOP</span>
            </div>
          </div>
          <p className="text-[#a0a0a0] mb-6 max-w-md text-base" style={{ fontFamily: 'Georgia, serif' }}>
            A hidden gem in Menomonee Falls offering a remarkable mix of traditional techniques and modern grooming solutions.
          </p>
          <div className="flex gap-4">
            <a href="https://www.facebook.com/gentsclassicbarbershop/" target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-zinc-800 flex items-center justify-center text-zinc-300 hover:bg-[#d4af37] hover:text-black transition-all rounded" data-testid="footer-facebook">
              <Facebook size={20} />
            </a>
            <a href="https://www.instagram.com/gentsclassicbarbershop/" target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-zinc-800 flex items-center justify-center text-zinc-300 hover:bg-[#d4af37] hover:text-black transition-all rounded" data-testid="footer-instagram">
              <Instagram size={20} />
            </a>
            <a href="https://www.tiktok.com/@gentsclassicbarbershop" target="_blank" rel="noopener noreferrer" className="w-11 h-11 bg-zinc-800 flex items-center justify-center text-zinc-300 hover:bg-[#d4af37] hover:text-black transition-all rounded" data-testid="footer-tiktok">
              <svg viewBox="0 0 24 24" aria-hidden="true" className="w-5 h-5 fill-current">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.26V2h-3.2v13.03a2.9 2.9 0 1 1-2-2.76V9.01a6.1 6.1 0 1 0 5.2 6.02V8.36a8.08 8.08 0 0 0 4.77 1.55V6.69z" />
              </svg>
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="text-sm tracking-widest text-[#d4af37] mb-4 font-bold" style={{ fontFamily: 'Georgia, serif' }}>QUICK LINKS</h4>
          <div className="space-y-3">
            {["/", "/about", "/services", "/gallery", "/reviews", "/contact"].map((path, i) =>
          <Link key={path} to={path} className="block text-[#a0a0a0] hover:text-white transition-colors text-sm" style={{ fontFamily: 'Georgia, serif' }}>
                {["Home", "About Us", "Services", "Gallery", "Reviews", "Contact"][i]}
              </Link>
          )}
          </div>
        </div>

        {/* Contact */}
        <div>
          <h4 className="text-sm tracking-widest text-[#d4af37] mb-4 font-bold" style={{ fontFamily: 'Georgia, serif' }}>CONTACT</h4>
          <div className="space-y-3 text-sm" style={{ fontFamily: 'Georgia, serif' }}>
            <p className="text-[#a0a0a0]">{BUSINESS_INFO.address}<br />{BUSINESS_INFO.city}</p>
            <a href={`tel:${BUSINESS_INFO.phoneClean}`} className="block text-[#d4af37] hover:text-[#e5c04b] text-lg font-bold">{BUSINESS_INFO.phone}</a>
            <p className="text-[#808080]">Tue-Fri: 9AM-6PM<br />Sat: 8AM-2PM</p>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
        <p className="text-[#606060] text-sm" style={{ fontFamily: 'Georgia, serif' }}>© {new Date().getFullYear()} Gent's Classic Barber Shop. All rights reserved.</p>
        <p className="text-[#505050] text-xs" style={{ fontFamily: 'Georgia, serif' }}>Crafted with precision, just like our cuts.</p>
      </div>
    </div>
  </footer>;


// HomePage
const HomePage = ({ services }) => {
  const today = new Date().getDay();
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const heroBackground = `linear-gradient(to bottom, rgba(28,28,28,0.3) 0%, rgba(28,28,28,0.55) 60%, rgba(28,28,28,0.85) 100%), url('${BG_HERO}')`;

  return (
    <motion.div {...pageTransition}>
      {/* Hero Section */}
      <section
        className="min-h-[85vh] md:min-h-screen relative flex items-center justify-center bg-[#0d0d0d]"
        style={{ backgroundImage: heroBackground, backgroundSize: 'cover', backgroundPosition: 'center' }}
        data-testid="hero-section">

        <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto pt-20 md:pt-24">
          {/* Badge - Georgia font */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="inline-flex items-center gap-1.5 sm:gap-2.5 px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 bg-[#d4af37] text-black mb-6 sm:mb-8">
            <Scissors className="w-3.5 h-3.5 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" />
            <span className="text-[10px] sm:text-[11px] md:text-xs tracking-[1.5px] sm:tracking-[2px] md:tracking-[3px] font-extrabold" style={{ fontFamily: 'Georgia, serif' }}>WALK-INS WELCOME</span>
          </motion.div>

          {/* SEO headings kept for search engines while preserving the original visual hero line */}
          <h1 className="sr-only">Gent's Classic Barber Shop</h1>
          <h2 className="sr-only">Traditional Barbershop & Grooming in Menomonee Falls, Wisconsin</h2>

          {/* Main Title */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mb-6 leading-tight" data-testid="hero-title">
            <span className="font-bold text-4xl sm:text-5xl text-white" style={{ fontFamily: 'Georgia, serif' }}>Classic </span>
            <span className="font-bold text-4xl sm:text-5xl italic text-[#E5C04B]" style={{ fontFamily: 'Georgia, serif' }}>Craft.</span>
            <span className="block font-bold text-4xl sm:text-5xl mt-1 sm:mt-2 text-white" style={{ fontFamily: 'Georgia, serif' }}>Refined Grooming.</span>
          </motion.div>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="max-w-2xl text-sm sm:text-base italic mb-10 sm:mb-12 mx-auto text-[#e0e0e0]" style={{ fontFamily: 'Georgia, serif' }}>
            A premium neighborhood barbershop delivering precise cuts, meticulous beard work, and consistently polished results.
          </motion.p>

          {/* CTA - Only phone, no Book Appointment */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href={`tel:${BUSINESS_INFO.phoneClean}`} data-testid="hero-call-btn">
              <button className="hover:bg-zinc-100 transition-all flex items-center justify-center font-semibold text-sm sm:text-base px-8 sm:px-10 py-3.5 sm:py-4 gap-3 text-black bg-white w-[86vw] max-w-[300px] sm:w-auto" style={{ fontFamily: 'Tahoma, sans-serif' }}>
                <Phone size={20} />
                +1 {BUSINESS_INFO.phone}
              </button>
            </a>
            <Link to="/services">
              <button className="border-2 border-[#a0a0a0] hover:border-white hover:bg-white/5 transition-all flex items-center justify-center font-bold text-sm sm:text-base px-8 sm:px-10 py-3.5 sm:py-4 gap-2 text-white w-[86vw] max-w-[300px] sm:w-auto" style={{ fontFamily: 'Georgia, serif' }}>
                VIEW SERVICES <ArrowRight size={18} />
              </button>
            </Link>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="absolute bottom-5 sm:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[#a0a0a0]">
          <span className="text-xs tracking-widest" style={{ fontFamily: 'Georgia, serif' }}>SCROLL</span>
          <ChevronDown size={20} className="animate-bounce" />
        </motion.div>
      </section>

      {/* Features Strip */}
      <section className="py-16 bg-[#f5f2eb] border-y border-[#d4af37]/20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {FEATURES.map((feature, i) =>
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="text-center">
                <feature.icon className="w-10 h-10 text-[#8b7327] mx-auto mb-3" />
                <h3 className="text-sm tracking-wider text-[#2c2c2c] font-bold mb-1" style={{ fontFamily: 'Georgia, serif' }}>{feature.title}</h3>
                <p className="text-[#666] text-sm" style={{ fontFamily: 'Georgia, serif' }}>{feature.desc}</p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* About Teaser - Matching reference "Where Tradition Meets Craft" */}
      <section className="py-24 bg-[#f7f4ed]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image with badge */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
              <div className="border-4 border-[#d4af37] p-2">
                <img src={TEAM_PHOTO_URL} alt="Team at Gent's Classic Barber Shop" className="w-full h-[450px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#1c1c1c] text-white px-6 py-4 shadow-xl">
                <div className="text-3xl font-bold text-[#d4af37]" style={{ fontFamily: 'Georgia, serif' }}>5★</div>
                <div className="text-sm text-[#a0a0a0]" style={{ fontFamily: 'Georgia, serif' }}>Rated in Falls</div>
              </div>
            </motion.div>

            {/* Text */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <motion.span variants={fadeInUp} className="text-sm tracking-[4px] text-[#8b7327] font-bold block mb-4" style={{ fontFamily: 'Georgia, serif' }}>OUR STORY</motion.span>
              <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl text-[#2c2c2c] mb-6" style={{ fontFamily: 'Georgia, serif' }}>
                Where <em className="text-[#d4af37] italic">Tradition</em><br />Meets Craft
              </motion.h2>
              <motion.p variants={fadeInUp} className="text-[#555] mb-6 leading-relaxed text-lg" style={{ fontFamily: 'Georgia, serif' }}>
                Gent's Classic Barber Shop brings old-school barbering values into the modern era. Every cut should be precise, every shave luxurious, and every visit an experience worth repeating.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-[#666] mb-8 leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
                Proudly serving the Menomonee Falls community with artistry and attention to detail that sets us apart.
              </motion.p>
              
              {/* Stats */}
              <motion.div variants={fadeInUp} className="grid grid-cols-4 gap-4 mb-8">
                {[
                { num: "500+", label: "Happy Clients" },
                { num: "5★", label: "Avg Rating" },
                { num: "10+", label: "Years Exp." },
                { num: "100%", label: "Satisfaction" }].
                map((stat, i) =>
                <div key={i} className="text-center">
                    <div className="text-2xl font-bold text-[#d4af37]" style={{ fontFamily: 'Georgia, serif' }}>{stat.num}</div>
                    <div className="text-xs text-[#888]" style={{ fontFamily: 'Georgia, serif' }}>{stat.label}</div>
                  </div>
                )}
              </motion.div>

              <motion.div variants={fadeInUp}>
                <Link to="/about">
                  <button className="btn-gold flex items-center gap-2">
                    Our Story <ArrowRight size={18} />
                  </button>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-[#1c1c1c]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.span variants={fadeInUp} className="text-sm tracking-[4px] text-[#d4af37] font-bold" style={{ fontFamily: 'Georgia, serif' }}>PRICING</motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl text-white mt-4 mb-12" style={{ fontFamily: 'Georgia, serif' }}>
              Our <em className="text-[#d4af37] italic">Services</em>
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid md:grid-cols-2 gap-x-16 gap-y-0 mb-12">
            {services.slice(0, 6).map((service) =>
            <motion.div key={service.id} variants={fadeInUp} className="service-item group">
                <span className="service-name group-hover:text-[#d4af37] transition-colors">{service.name}</span>
                <span className="service-dots" />
                <span className="service-price">{service.price}</span>
              </motion.div>
            )}
          </motion.div>
          <Link to="/services">
            <button className="btn-gold text-lg px-10 py-4">VIEW ALL SERVICES</button>
          </Link>
        </div>
      </section>

      {/* Hours & Map Section */}
      <section className="py-24 bg-[#f5f2eb]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-sm tracking-[4px] text-[#8b7327] font-bold" style={{ fontFamily: 'Georgia, serif' }}>VISIT US</span>
            <h2 className="text-4xl md:text-5xl text-[#2c2c2c] mt-4" style={{ fontFamily: 'Georgia, serif' }}>Hours & <em className="text-[#d4af37] italic">Location</em></h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Hours */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-white border border-[#e0d9cc] p-8 shadow-lg">
              <h3 className="text-lg tracking-widest text-[#2c2c2c] mb-6 flex items-center gap-3 font-bold" style={{ fontFamily: 'Georgia, serif' }}>
                <Clock className="text-[#d4af37]" size={24} />
                BUSINESS HOURS
              </h3>
              <div className="space-y-0">
                {BUSINESS_INFO.hours.map((item) =>
                <div key={item.day} className={`flex justify-between py-4 border-b border-[#f0ebe0] last:border-0 ${item.day === dayNames[today] ? "bg-[#fdf8eb] -mx-4 px-4" : ""}`} data-testid={`home-hours-${item.day.toLowerCase()}`}>
                    <span className={`font-semibold ${item.day === dayNames[today] ? "text-[#8b7327]" : "text-[#444]"}`} style={{ fontFamily: 'Georgia, serif' }}>
                      {item.day}
                      {item.day === dayNames[today] && <span className="ml-2 text-xs bg-[#d4af37] text-white px-2 py-0.5 rounded">TODAY</span>}
                    </span>
                    <span className={item.closed ? "text-[#999]" : "text-[#555] font-medium"} style={{ fontFamily: 'Georgia, serif' }}>{item.time}</span>
                  </div>
                )}
              </div>
              <div className="mt-6 pt-6 border-t border-[#e0d9cc]">
                <a href={`tel:${BUSINESS_INFO.phoneClean}`} className="btn-gold w-full flex items-center justify-center gap-3 text-lg py-4">
                  <Phone size={20} />
                  CALL TO BOOK
                </a>
              </div>
            </motion.div>

            {/* Map */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-white border border-[#e0d9cc] shadow-lg overflow-hidden">
              <div className="p-6 border-b border-[#e0d9cc]">
                <h3 className="text-lg tracking-widest text-[#2c2c2c] flex items-center gap-3 font-bold" style={{ fontFamily: 'Georgia, serif' }}>
                  <MapPin className="text-[#d4af37]" size={24} />
                  FIND US
                </h3>
                <p className="text-[#666] mt-2" style={{ fontFamily: 'Georgia, serif' }}>{BUSINESS_INFO.address}<br />{BUSINESS_INFO.city}</p>
              </div>
              <div className="h-[350px]" data-testid="home-map">
                <iframe
                  src={GOOGLE_MAPS_EMBED_URL}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Location" />

              </div>
              <div className="px-6 py-4 border-t border-[#e0d9cc] bg-[#faf9f6]">
                <a href={GOOGLE_MAPS_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#8b7327] hover:text-[#d4af37] font-semibold tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
                  <Star size={16} />
                  READ GOOGLE REVIEWS
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-[#1c1c1c]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-sm tracking-[4px] text-[#d4af37] font-bold" style={{ fontFamily: 'Georgia, serif' }}>CLIENT LOVE</span>
            <h2 className="text-4xl md:text-5xl text-[#f7f4ed] mt-4" style={{ fontFamily: 'Georgia, serif' }}>What They're <em className="text-[#d4af37] italic">Saying</em></h2>
            <div className="flex items-center justify-center gap-3 mt-4">
              <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={24} fill="#d4af37" className="text-[#d4af37]" />)}</div>
              <span className="text-white text-2xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>{BUSINESS_INFO.rating}</span>
              <span className="text-[#888]" style={{ fontFamily: 'Georgia, serif' }}>({BUSINESS_INFO.reviewCount} reviews)</span>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.slice(0, 3).map((t, i) =>
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-[#252525] border border-[#d4af37]/20 p-8" data-testid={`testimonial-${i}`}>
                <div className="flex mb-4 text-[#d4af37]">★★★★★</div>
                <p className="text-[#d0d0d0] mb-6 leading-relaxed text-lg italic" style={{ fontFamily: 'Georgia, serif' }}>"{t.text}"</p>
                <div className="flex items-center gap-3 border-t border-white/10 pt-4">
                  <div className="w-12 h-12 rounded-full bg-[#d4af37]/20 flex items-center justify-center text-[#d4af37] text-lg font-bold" style={{ fontFamily: 'Georgia, serif' }}>{t.name[0]}</div>
                  <div>
                    <div className="text-white font-semibold" style={{ fontFamily: 'Georgia, serif' }}>{t.name}</div>
                    <div className="text-[#888] text-sm" style={{ fontFamily: 'Georgia, serif' }}>{t.date}</div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
          <div className="text-center mt-12">
            <Link to="/reviews"><button className="btn-outline-gold text-lg px-10 py-4">SEE ALL REVIEWS</button></Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden bg-[#0d0d0d]">
        <div className="absolute inset-0">
          <img src={SHOP_EXTERIOR_URL} alt="" className="w-full h-full object-cover opacity-20" />
        </div>
        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
          <Scissors className="w-16 h-16 text-[#d4af37] mx-auto mb-6" />
          <h2 className="text-4xl md:text-5xl text-white mb-6" style={{ fontFamily: 'Georgia, serif' }}>Ready for a <em className="text-[#d4af37] italic">Fresh Look?</em></h2>
          <p className="text-[#a0a0a0] mb-10 max-w-xl mx-auto text-lg" style={{ fontFamily: 'Georgia, serif' }}>Walk in during business hours or give us a call. We're here to make you look and feel your best.</p>
          <a href={`tel:${BUSINESS_INFO.phoneClean}`}>
            <button className="btn-gold text-xl px-12 py-5 flex items-center gap-3 mx-auto">
              <Phone size={24} />
              {BUSINESS_INFO.phone}
            </button>
          </a>
        </div>
      </section>
    </motion.div>);

};

// About Page with background - matching index.html reference
const AboutPage = () => (
  <motion.div {...pageTransition}>
    {/* Hero Banner */}
    <section className="pt-32 pb-20 relative" style={{ backgroundImage: `linear-gradient(135deg, rgba(28,28,28,0.85), rgba(28,28,28,0.6)), url('${BG_BARBER_WORK}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="max-w-7xl mx-auto px-6 text-center">
        <span className="text-sm tracking-[4px] text-[#d4af37] font-bold" style={{ fontFamily: 'Georgia, serif' }}>WHO WE ARE</span>
        <h1 className="text-5xl md:text-6xl text-white mt-4" style={{ fontFamily: 'Georgia, serif' }}>Our <em className="text-[#e5c04b] italic">Story</em></h1>
        <p className="text-[#d0d0d0] mt-4 max-w-2xl mx-auto text-lg" style={{ fontFamily: 'Georgia, serif' }}>Craft, community, and classic barbering</p>
      </div>
    </section>

    {/* Where Tradition Meets Craft - Similar to index.html */}
    <section className="py-24 bg-[#f7f4ed]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Image with floating badge */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
            <div className="border-4 border-[#d4af37] p-2">
              <img src={TEAM_PHOTO_URL} alt="Team at Gent's Classic Barber Shop" className="w-full h-[500px] object-cover" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#1c1c1c] text-white px-8 py-5 shadow-xl">
              <div className="text-4xl font-bold text-[#d4af37]" style={{ fontFamily: 'Georgia, serif' }}>5★</div>
              <div className="text-sm text-[#a0a0a0]" style={{ fontFamily: 'Georgia, serif' }}>Rated in Falls</div>
            </div>
          </motion.div>

          {/* Text Content */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
            <motion.span variants={fadeInUp} className="text-sm tracking-[4px] text-[#8b7327] font-bold block mb-4" style={{ fontFamily: 'Georgia, serif' }}>OUR STORY</motion.span>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl text-[#2c2c2c] mb-6" style={{ fontFamily: 'Georgia, serif' }}>
              Where <em className="text-[#d4af37] italic">Tradition</em><br />Meets Craft
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#555] mb-6 leading-relaxed text-lg" style={{ fontFamily: 'Georgia, serif' }}>
              Gent's Classic Barber Shop brings old-school barbering values into the modern era. We believe every cut should be precise, every shave luxurious, and every visit an experience worth repeating.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-[#666] mb-8 leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
              Proudly serving the Menomonee Falls community with artistry and attention to detail that sets us apart. From first haircuts to weekly regulars — every client is treated with the same level of respect and attention.
            </motion.p>
            
            {/* Stats Row */}
            <motion.div variants={fadeInUp} className="grid grid-cols-4 gap-4 mb-8 border-t border-[#e0d9cc] pt-8">
              {[
                { num: "500+", label: "Happy Clients" },
                { num: "5★", label: "Avg Rating" },
                { num: "10+", label: "Years Exp." },
                { num: "100%", label: "Satisfaction" },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-[#d4af37]" style={{ fontFamily: 'Georgia, serif' }}>{stat.num}</div>
                  <div className="text-xs text-[#888] mt-1" style={{ fontFamily: 'Georgia, serif' }}>{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>

    {/* Our Values - Diamond icons like index.html */}
    <section className="py-24 bg-[#1c1c1c]">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-sm tracking-[4px] text-[#d4af37] font-bold" style={{ fontFamily: 'Georgia, serif' }}>OUR VALUES</span>
          <h2 className="text-4xl md:text-5xl text-white mt-4" style={{ fontFamily: 'Georgia, serif' }}>What We <em className="text-[#d4af37] italic">Stand For</em></h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: "✂", title: "Precision & Skill", desc: "Every cut is executed with meticulous attention to detail. We don't just cut hair — we craft styles that enhance your best features." },
            { icon: "♦", title: "Welcoming Environment", desc: "Our shop is more than a barbershop — it's a space where you can relax, chat, and leave feeling like the best version of yourself." },
            { icon: "★", title: "Community First", desc: "We're deeply rooted in Menomonee Falls. We grow alongside the community we serve and take pride in being your local shop." },
          ].map((value, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }} className="text-center p-8 border border-[#d4af37]/20 bg-[#252525]">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center text-3xl text-[#d4af37] bg-[#d4af37]/10 rotate-45">
                <span className="-rotate-45">{value.icon}</span>
              </div>
              <h3 className="text-xl text-white mb-4 font-bold" style={{ fontFamily: 'Georgia, serif' }}>{value.title}</h3>
              <p className="text-[#a0a0a0] leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>{value.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Meet The Team */}
    <section className="py-24 bg-[#f7f4ed]">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-sm tracking-[4px] text-[#8b7327] font-bold" style={{ fontFamily: 'Georgia, serif' }}>MEET THE TEAM</span>
          <h2 className="text-4xl md:text-5xl text-[#2c2c2c] mt-4" style={{ fontFamily: 'Georgia, serif' }}>Our <em className="text-[#d4af37] italic">Barbers</em></h2>
        </div>
        <div className="grid md:grid-cols-2 gap-12">
          {BARBERS.map((barber, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.2 }} className="bg-white border border-[#e0d9cc] p-8 text-center shadow-lg">
              <div className="w-28 h-28 bg-[#f5f2eb] rounded-full flex items-center justify-center text-[#d4af37] text-5xl mb-6 mx-auto font-bold border-4 border-[#d4af37]" style={{ fontFamily: 'Georgia, serif' }}>{barber.name[0]}</div>
              <h3 className="text-3xl text-[#2c2c2c] mb-1" style={{ fontFamily: 'Georgia, serif' }}>{barber.name}</h3>
              <p className="text-sm tracking-widest text-[#d4af37] mb-4 font-bold" style={{ fontFamily: 'Georgia, serif' }}>{barber.role}</p>
              <p className="text-[#666] mb-4 text-lg leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>{barber.bio}</p>
              <div className="border-t border-[#e0d9cc] pt-4 mt-4">
                <span className="text-[#888]" style={{ fontFamily: 'Georgia, serif' }}>Specialty: </span>
                <span className="text-[#2c2c2c] font-semibold" style={{ fontFamily: 'Georgia, serif' }}>{barber.specialty}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Shop Photos */}
    <section className="py-24 bg-[#1c1c1c]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-sm tracking-[4px] text-[#d4af37] font-bold" style={{ fontFamily: 'Georgia, serif' }}>OUR SPACE</span>
          <h2 className="text-4xl text-white mt-4" style={{ fontFamily: 'Georgia, serif' }}>The <em className="text-[#d4af37] italic">Shop</em></h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="border-4 border-[#d4af37] p-2">
            <img src={SHOP_EXTERIOR_URL} alt="Interior of Gent's Classic Barber Shop in Menomonee Falls" className="w-full h-80 object-cover" />
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="border-4 border-[#d4af37] p-2">
            <img src={SHOP_INTERIOR_URL} alt="Interior of Gent's Classic Barber Shop in Menomonee Falls" className="w-full h-80 object-cover" />
          </motion.div>
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="py-20 bg-[#d4af37]">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl text-[#1c1c1c] mb-4" style={{ fontFamily: 'Georgia, serif' }}>Ready to Experience the Difference?</h2>
        <p className="text-[#4a4a4a] mb-8 text-lg" style={{ fontFamily: 'Georgia, serif' }}>Walk in or call ahead — we're here for you.</p>
        <a href={`tel:${BUSINESS_INFO.phoneClean}`}>
          <button className="bg-[#1c1c1c] text-white px-10 py-4 text-lg flex items-center gap-3 mx-auto font-bold hover:bg-[#333] transition-all" style={{ fontFamily: 'Georgia, serif' }}>
            <Phone size={22} />
            {BUSINESS_INFO.phone}
          </button>
        </a>
      </div>
    </section>
  </motion.div>
);


// Services Page with background
const ServicesPage = ({ services }) =>
<motion.div {...pageTransition}>
    <section className="pt-32 pb-20 relative" style={{ backgroundImage: `linear-gradient(135deg, rgba(28,28,28,0.85), rgba(28,28,28,0.6)), url('${BG_BEARD}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="max-w-7xl mx-auto px-6 text-center">
        <span className="text-sm tracking-[4px] text-[#d4af37] font-bold" style={{ fontFamily: 'Georgia, serif' }}>WHAT WE OFFER</span>
        <h1 className="text-5xl md:text-6xl text-white mt-4" style={{ fontFamily: 'Georgia, serif' }}>Our <em className="text-[#e5c04b] italic">Services</em></h1>
        <p className="text-[#d0d0d0] mt-4 max-w-2xl mx-auto text-lg" style={{ fontFamily: 'Georgia, serif' }}>Premium grooming for the modern gentleman</p>
      </div>
    </section>

    <section className="py-24 bg-[#f7f4ed]">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-6">
          {services.map((service, i) =>
        <motion.div key={service.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="bg-white border border-[#e0d9cc] p-8 group hover:border-[#d4af37] hover:shadow-lg transition-all" data-testid={`service-card-${i}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-2xl text-[#2c2c2c] group-hover:text-[#8b7327] transition-colors" style={{ fontFamily: 'Georgia, serif' }}>{service.name}</h3>
                  {service.popular && <span className="inline-block mt-2 text-[10px] tracking-wider bg-[#d4af37] text-white px-3 py-1">POPULAR</span>}
                </div>
                <span className="text-3xl text-[#d4af37] font-bold" style={{ fontFamily: 'Georgia, serif' }}>{service.price}</span>
              </div>
              <p className="text-[#666] mb-4 text-lg" style={{ fontFamily: 'Georgia, serif' }}>{service.description}</p>
              <div className="flex items-center gap-2 text-[#888] text-sm">
                <Clock size={16} />
                <span style={{ fontFamily: 'Georgia, serif' }}>{service.duration}</span>
              </div>
            </motion.div>
        )}
        </div>

        <div className="mt-16 bg-[#1c1c1c] p-12 text-center">
          <Scissors className="w-12 h-12 text-[#d4af37] mx-auto mb-4" />
          <h3 className="text-3xl text-white mb-3" style={{ fontFamily: 'Georgia, serif' }}>Ready to Book?</h3>
          <p className="text-[#a0a0a0] mb-6 text-lg" style={{ fontFamily: 'Georgia, serif' }}>Walk-ins welcome or call ahead to schedule your appointment.</p>
          <a href={`tel:${BUSINESS_INFO.phoneClean}`}>
            <button className="btn-gold text-xl flex items-center gap-3 mx-auto px-10 py-4">
              <Phone size={22} />
              {BUSINESS_INFO.phone}
            </button>
          </a>
        </div>
      </div>
    </section>
  </motion.div>;


// Gallery Page with background
const GalleryPage = ({ galleryImages }) => {
  const [filter, setFilter] = useState("All");
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const touchEndX = useRef(0);
  const touchEndY = useRef(0);
  const categories = ["All", ...Array.from(new Set(galleryImages.map((img) => img.category))).filter((category) => category.toLowerCase() !== "fades")];
  const filteredImages = filter === "All" ? galleryImages : galleryImages.filter((img) => img.category === filter);
  const activeImage = filteredImages[selectedImageIndex] || null;

  useEffect(() => {
    if (filter !== "All" && !galleryImages.some((img) => img.category === filter)) {
      setFilter("All");
    }
  }, [filter, galleryImages]);

  const handleOpenImage = (index) => {
    setSelectedImageIndex(index);
    setIsLightboxOpen(true);
  };

  const handlePrevImage = useCallback(() => {
    if (filteredImages.length < 2) return;
    setSelectedImageIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
  }, [filteredImages.length]);

  const handleNextImage = useCallback(() => {
    if (filteredImages.length < 2) return;
    setSelectedImageIndex((prev) => (prev + 1) % filteredImages.length);
  }, [filteredImages.length]);

  useEffect(() => {
    if (!isLightboxOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft") {
        handlePrevImage();
      }
      if (event.key === "ArrowRight") {
        handleNextImage();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLightboxOpen, handlePrevImage, handleNextImage]);

  useEffect(() => {
    if (selectedImageIndex >= filteredImages.length) {
      setSelectedImageIndex(0);
    }
    if (filteredImages.length === 0 && isLightboxOpen) {
      setIsLightboxOpen(false);
    }
  }, [filteredImages.length, selectedImageIndex, isLightboxOpen]);

  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    touchEndX.current = touch.clientX;
    touchEndY.current = touch.clientY;
  };

  const handleTouchMove = (event) => {
    const touch = event.touches[0];
    touchEndX.current = touch.clientX;
    touchEndY.current = touch.clientY;
  };

  const handleTouchEnd = () => {
    const deltaX = touchEndX.current - touchStartX.current;
    const deltaY = touchEndY.current - touchStartY.current;

    // Require a mostly horizontal gesture so normal vertical scroll still feels natural.
    if (Math.abs(deltaX) > 45 && Math.abs(deltaX) > Math.abs(deltaY) * 1.3) {
      if (deltaX > 0) {
        handlePrevImage();
      } else {
        handleNextImage();
      }
    }
  };

  return (
    <motion.div {...pageTransition}>
      <section className="pt-32 pb-20 relative" style={{ backgroundImage: `linear-gradient(135deg, rgba(28,28,28,0.88), rgba(28,28,28,0.55)), url('${BG_GALLERY}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-sm tracking-[4px] text-[#d4af37] font-bold" style={{ fontFamily: 'Georgia, serif' }}>PORTFOLIO</span>
          <h1 className="text-5xl md:text-6xl text-white mt-4" style={{ fontFamily: 'Georgia, serif' }}>The <em className="text-[#e5c04b] italic">Gallery</em></h1>
          <p className="text-[#d0d0d0] mt-4 max-w-2xl mx-auto text-lg" style={{ fontFamily: 'Georgia, serif' }}>See the craft up close</p>
        </div>
      </section>

      <section className="py-6 bg-white border-y border-[#e0d9cc] sticky top-[68px] z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) =>
            <button key={cat} onClick={() => setFilter(cat)} className={`text-sm tracking-wider px-6 py-2.5 transition-all rounded font-semibold ${filter === cat ? "bg-[#d4af37] text-white" : "bg-[#f5f2eb] text-[#555] hover:bg-[#e8e2d5]"}`} style={{ fontFamily: 'Georgia, serif' }} data-testid={`gallery-filter-${cat.toLowerCase()}`}>
                {cat.toUpperCase()}
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#f7f4ed]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div layout className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <AnimatePresence mode="popLayout">
              {filteredImages.map((img, i) =>
              <motion.div key={img.id} layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.3 }} className="gallery-item aspect-square" data-testid={`gallery-image-${i}`}>
                  <button
                    type="button"
                    className="gallery-tile-btn"
                    aria-label={`Open ${img.category} image ${i + 1}`}
                    onClick={() => handleOpenImage(i)}>

                    <img src={img.url} alt={`Gallery ${i + 1} - ${img.category}`} className="w-full h-full object-cover" loading="lazy" />
                    <div className="gallery-overlay">
                      <span className="text-[#d4af37] text-4xl font-light">+</span>
                    </div>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
          {filteredImages.length === 0 &&
          <div className="border border-[#d4af37]/20 bg-white px-6 py-10 text-center text-[#666]" style={{ fontFamily: 'Georgia, serif' }}>
              No gallery images in this category yet.
            </div>
          }
        </div>
      </section>

      <Dialog open={isLightboxOpen} onOpenChange={setIsLightboxOpen}>
        <DialogContent className="lightbox-content">
          {activeImage &&
          <>
              <DialogTitle className="sr-only">Gallery Image Preview</DialogTitle>
              <div className="lightbox-head">
                <span className="lightbox-category">{activeImage.category}</span>
                <span className="lightbox-count">{selectedImageIndex + 1}/{filteredImages.length}</span>
              </div>

              <div
              className="lightbox-image-wrap"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}>

                {filteredImages.length > 1 &&
              <button
                type="button"
                className="lightbox-nav lightbox-nav-prev"
                aria-label="Previous image"
                onClick={handlePrevImage}>

                    <ChevronLeft size={26} />
                  </button>
              }

                <img src={activeImage.url} alt={`${activeImage.category} preview ${selectedImageIndex + 1}`} className="lightbox-image" />

                {filteredImages.length > 1 &&
              <button
                type="button"
                className="lightbox-nav lightbox-nav-next"
                aria-label="Next image"
                onClick={handleNextImage}>

                    <ChevronRight size={26} />
                  </button>
              }
              </div>

              <p className="lightbox-caption">
                Swipe on mobile or use arrow keys on desktop to browse.
              </p>
            </>
          }
        </DialogContent>
      </Dialog>
    </motion.div>);

};


const AdminPage = ({ services, galleryImages, isContentLoading, onSaveServices, onAddGalleryImage, onDeleteGalleryImage }) => {
  const [editableServices, setEditableServices] = useState(services);
  const [newImage, setNewImage] = useState({ url: "", category: "" });
  const [isSavingServices, setIsSavingServices] = useState(false);
  const [isAddingImage, setIsAddingImage] = useState(false);
  const [removingImageId, setRemovingImageId] = useState("");

  useEffect(() => {
    setEditableServices(services);
  }, [services]);

  const servicePricesChanged = editableServices.some((service, index) => service.price !== services[index]?.price);

  const handleSaveServices = async () => {
    setIsSavingServices(true);
    try {
      await onSaveServices(editableServices);
      toast.success("Service prices updated.");
    } catch {
      toast.error("Failed to save service prices.");
    }
    setIsSavingServices(false);
  };

  const handleAddImage = async (event) => {
    event.preventDefault();
    setIsAddingImage(true);
    try {
      await onAddGalleryImage(newImage);
      setNewImage({ url: "", category: "" });
      toast.success("Gallery image added.");
    } catch {
      toast.error("Failed to add gallery image.");
    }
    setIsAddingImage(false);
  };

  const handleRemoveImage = async (imageId) => {
    setRemovingImageId(imageId);
    try {
      await onDeleteGalleryImage(imageId);
      toast.success("Gallery image removed.");
    } catch {
      toast.error("Failed to remove gallery image.");
    }
    setRemovingImageId("");
  };

  return (
    <motion.div {...pageTransition}>
      <section className="pt-32 pb-20 relative" style={{ backgroundImage: `linear-gradient(135deg, rgba(28,28,28,0.92), rgba(28,28,28,0.72)), url('${BG_SHOP}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-sm tracking-[4px] text-[#d4af37] font-bold" style={{ fontFamily: 'Georgia, serif' }}>ADMIN PANEL</span>
          <h1 className="text-5xl md:text-6xl text-white mt-4" style={{ fontFamily: 'Georgia, serif' }}><em className="text-[#e5c04b] italic">Manage</em> Site Content</h1>
          <p className="text-[#d0d0d0] mt-4 max-w-2xl mx-auto text-lg" style={{ fontFamily: 'Georgia, serif' }}>
            Add or remove gallery images and update prices without editing code.
          </p>
        </div>
      </section>

      <section className="py-24 bg-[#f7f4ed]">
        <div className="max-w-7xl mx-auto px-6 grid xl:grid-cols-[1.1fr_0.9fr] gap-10">
          <div className="bg-white border border-[#e0d9cc] shadow-sm">
            <div className="border-b border-[#e0d9cc] px-8 py-6 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-3xl text-[#2c2c2c]" style={{ fontFamily: 'Georgia, serif' }}>Service Pricing</h2>
                <p className="text-[#777] mt-2" style={{ fontFamily: 'Georgia, serif' }}>Edit prices and save the full service list in one update.</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-[#fdf8eb] flex items-center justify-center text-[#d4af37]">
                <Settings2 size={24} />
              </div>
            </div>

            <div className="p-8 space-y-4">
              {editableServices.map((service) =>
              <div key={service.id} className="grid md:grid-cols-[1.5fr_140px] gap-4 items-start border border-[#eee6d8] bg-[#fcfbf8] p-4">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl text-[#2c2c2c]" style={{ fontFamily: 'Georgia, serif' }}>{service.name}</h3>
                      {service.popular && <span className="text-[10px] tracking-widest bg-[#d4af37] text-white px-2 py-1">POPULAR</span>}
                    </div>
                    <p className="text-[#666] mb-2" style={{ fontFamily: 'Georgia, serif' }}>{service.description}</p>
                    <p className="text-sm text-[#999]" style={{ fontFamily: 'Georgia, serif' }}>{service.duration}</p>
                  </div>
                  <label className="block">
                    <span className="block text-xs tracking-[3px] text-[#8b7327] font-bold mb-2" style={{ fontFamily: 'Georgia, serif' }}>PRICE</span>
                    <input
                      type="text"
                      value={service.price}
                      onChange={(event) => setEditableServices((prev) => prev.map((item) => item.id === service.id ? { ...item, price: event.target.value } : item))}
                      className="w-full border border-[#d9cfbe] bg-white px-4 py-3 text-[#2c2c2c] focus:border-[#d4af37] focus:outline-none"
                      style={{ fontFamily: 'Georgia, serif' }} />
                  </label>
                </div>
              )}

              <Button
                type="button"
                onClick={handleSaveServices}
                disabled={isSavingServices || !servicePricesChanged}
                className="w-full md:w-auto bg-[#1c1c1c] hover:bg-[#2d2d2d] text-white px-8 py-6 rounded-none"
                style={{ fontFamily: 'Georgia, serif' }}>

                <span className="flex items-center gap-3 tracking-[3px] text-sm">
                  <Save size={18} />
                  {isSavingServices ? "SAVING PRICES..." : "SAVE PRICES"}
                </span>
              </Button>
            </div>
          </div>

          <div className="space-y-8">
            <div className="bg-[#1c1c1c] text-white p-8">
              <div className="flex items-center gap-3 mb-4">
                <ImagePlus className="text-[#d4af37]" size={24} />
                <h2 className="text-3xl" style={{ fontFamily: 'Georgia, serif' }}>Gallery Manager</h2>
              </div>
              <p className="text-[#bbb] mb-6" style={{ fontFamily: 'Georgia, serif' }}>
                Add new images by URL and remove existing shots from the live gallery.
              </p>

              <form onSubmit={handleAddImage} className="space-y-4">
                <input
                  type="url"
                  value={newImage.url}
                  onChange={(event) => setNewImage((prev) => ({ ...prev, url: event.target.value }))}
                  placeholder="https://example.com/barber-cut.jpg"
                  className="w-full bg-[#111] border border-[#4a412b] px-4 py-3 text-white focus:border-[#d4af37] focus:outline-none"
                  style={{ fontFamily: 'Georgia, serif' }}
                  required />
                <input
                  type="text"
                  value={newImage.category}
                  onChange={(event) => setNewImage((prev) => ({ ...prev, category: event.target.value }))}
                  placeholder="Fades, Haircuts, Beard, Shop..."
                  className="w-full bg-[#111] border border-[#4a412b] px-4 py-3 text-white focus:border-[#d4af37] focus:outline-none"
                  style={{ fontFamily: 'Georgia, serif' }}
                  required />
                <Button
                  type="submit"
                  disabled={isAddingImage}
                  className="w-full bg-[#d4af37] hover:bg-[#b8962e] text-black py-6 rounded-none"
                  style={{ fontFamily: 'Georgia, serif' }}>

                  <span className="flex items-center justify-center gap-3 tracking-[3px] text-sm">
                    <ImagePlus size={18} />
                    {isAddingImage ? "ADDING IMAGE..." : "ADD IMAGE"}
                  </span>
                </Button>
              </form>
            </div>

            <div className="bg-white border border-[#e0d9cc] p-6">
              <div className="flex items-center justify-between gap-4 mb-5">
                <h3 className="text-2xl text-[#2c2c2c]" style={{ fontFamily: 'Georgia, serif' }}>Current Gallery</h3>
                <span className="text-sm text-[#888]" style={{ fontFamily: 'Georgia, serif' }}>{galleryImages.length} images</span>
              </div>

              <div className="space-y-4 max-h-[760px] overflow-y-auto pr-1">
                {galleryImages.map((image) =>
                <div key={image.id} className="grid grid-cols-[88px_1fr_auto] gap-4 items-center border border-[#eee6d8] p-3 bg-[#fcfbf8]">
                    <img src={image.url} alt={image.category} className="w-[88px] h-[88px] object-cover" />
                    <div className="min-w-0">
                      <div className="text-sm tracking-[3px] text-[#8b7327] font-bold mb-1" style={{ fontFamily: 'Georgia, serif' }}>{image.category.toUpperCase()}</div>
                      <p className="text-sm text-[#666] truncate" style={{ fontFamily: 'Georgia, serif' }}>{image.url}</p>
                    </div>
                    <Button
                      type="button"
                      onClick={() => handleRemoveImage(image.id)}
                      disabled={removingImageId === image.id}
                      className="bg-transparent hover:bg-[#1c1c1c] text-[#1c1c1c] hover:text-white border border-[#d9cfbe] rounded-none px-4 py-3 shadow-none"
                      style={{ fontFamily: 'Georgia, serif' }}>

                      <span className="flex items-center gap-2 text-xs tracking-[2px]">
                        <Trash2 size={16} />
                        {removingImageId === image.id ? "REMOVING" : "REMOVE"}
                      </span>
                    </Button>
                  </div>
                )}

                {galleryImages.length === 0 &&
                <div className="border border-dashed border-[#d9cfbe] px-4 py-8 text-center text-[#777]" style={{ fontFamily: 'Georgia, serif' }}>
                    No gallery images yet.
                  </div>
                }
              </div>
            </div>

            <div className="border border-[#d4af37]/20 bg-[#fffaf0] px-6 py-5 text-[#65552b]" style={{ fontFamily: 'Georgia, serif' }}>
              {isContentLoading ? "Syncing saved site content..." : "Admin updates save immediately to the backend for future visits."}
            </div>
          </div>
        </div>
      </section>
    </motion.div>);
};

// Reviews Page
const ReviewsPage = () =>
<motion.div {...pageTransition}>
    <section className="pt-32 pb-20 bg-[#1c1c1c]">
      <div className="max-w-7xl mx-auto px-6 text-center">
        <span className="text-sm tracking-[4px] text-[#d4af37] font-bold" style={{ fontFamily: 'Georgia, serif' }}>TESTIMONIALS</span>
        <h1 className="text-5xl md:text-6xl text-white mt-4" style={{ fontFamily: 'Georgia, serif' }}>What Clients <em className="text-[#e5c04b] italic">Say</em></h1>
        <div className="flex items-center justify-center gap-4 mt-6">
          <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} size={28} fill="#d4af37" className="text-[#d4af37]" />)}</div>
          <span className="text-4xl text-white font-bold" style={{ fontFamily: 'Georgia, serif' }}>{BUSINESS_INFO.rating}</span>
        </div>
        <p className="text-[#a0a0a0] mt-2 text-lg" style={{ fontFamily: 'Georgia, serif' }}>{BUSINESS_INFO.reviewCount} reviews on Google</p>
      </div>
    </section>

    <section className="py-24 bg-[#f7f4ed]">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {TESTIMONIALS.map((t, i) =>
        <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="bg-white border border-[#e0d9cc] p-8 shadow-sm" data-testid={`review-${i}`}>
              <div className="flex justify-between items-start mb-4">
                <div className="text-[#d4af37]">★★★★★</div>
                <span className="text-xs tracking-wider text-[#888] bg-[#f5f2eb] px-2 py-1" style={{ fontFamily: 'Georgia, serif' }}>{t.service}</span>
              </div>
              <p className="text-[#555] mb-6 leading-relaxed text-lg italic" style={{ fontFamily: 'Georgia, serif' }}>"{t.text}"</p>
              <div className="flex items-center gap-3 border-t border-[#f0ebe0] pt-4">
                <div className="w-12 h-12 rounded-full bg-[#fdf8eb] flex items-center justify-center text-[#8b7327] text-lg font-bold" style={{ fontFamily: 'Georgia, serif' }}>{t.name[0]}</div>
                <div>
                  <div className="text-[#2c2c2c] font-semibold" style={{ fontFamily: 'Georgia, serif' }}>{t.name}</div>
                  <div className="text-[#888] text-sm" style={{ fontFamily: 'Georgia, serif' }}>{t.date}</div>
                </div>
              </div>
            </motion.div>
        )}
        </div>

        <div className="mt-16 text-center">
          <div className="inline-block bg-[#1c1c1c] p-12">
            <Quote className="w-12 h-12 text-[#d4af37] mx-auto mb-4" />
            <h3 className="text-3xl text-white mb-3" style={{ fontFamily: 'Georgia, serif' }}>Had a Great Experience?</h3>
            <p className="text-[#a0a0a0] mb-6 text-lg" style={{ fontFamily: 'Georgia, serif' }}>We'd love to hear about it. Leave us a review on Google!</p>
            <a href="https://www.google.com/search?q=gents+classic+barber+shop+menomonee+falls" target="_blank" rel="noopener noreferrer">
              <button className="btn-gold text-lg px-10 py-4">WRITE A REVIEW</button>
            </a>
          </div>
        </div>
      </div>
    </section>
  </motion.div>;


// Contact Page with background
const ContactPage = () => {
  const [formData, setFormData] = useState({ name: "", phone: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: "", phone: "", message: "" });
    } catch {
      toast.error("Failed to send message. Please call us instead.");
    }
    setIsSubmitting(false);
  };

  const today = new Date().getDay();
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  return (
    <motion.div {...pageTransition}>
      <section className="pt-32 pb-20 relative" style={{ backgroundImage: `linear-gradient(135deg, rgba(28,28,28,0.85), rgba(28,28,28,0.55)), url('${BG_BARBER_WORK}')`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-sm tracking-[4px] text-[#d4af37] font-bold" style={{ fontFamily: 'Georgia, serif' }}>GET IN TOUCH</span>
          <h1 className="text-5xl md:text-6xl text-white mt-4" style={{ fontFamily: 'Georgia, serif' }}><em className="text-[#e5c04b] italic">Contact</em> Us</h1>
          <p className="text-[#d0d0d0] mt-4 max-w-2xl mx-auto text-lg" style={{ fontFamily: 'Georgia, serif' }}>We'd love to hear from you</p>
        </div>
      </section>

      <section className="py-24 bg-[#f7f4ed]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <div className="space-y-8 mb-12">
                <div className="flex items-start gap-5" data-testid="contact-address">
                  <div className="w-14 h-14 bg-[#fdf8eb] flex items-center justify-center flex-shrink-0 rounded"><MapPin className="text-[#8b7327]" size={26} /></div>
                  <div>
                    <h4 className="text-lg text-[#2c2c2c] tracking-widest mb-2 font-bold" style={{ fontFamily: 'Georgia, serif' }}>ADDRESS</h4>
                    <p className="text-[#666] text-lg" style={{ fontFamily: 'Georgia, serif' }}>{BUSINESS_INFO.address}<br />{BUSINESS_INFO.city}</p>
                  </div>
                </div>
                <div className="flex items-start gap-5" data-testid="contact-phone">
                  <div className="w-14 h-14 bg-[#fdf8eb] flex items-center justify-center flex-shrink-0 rounded"><Phone className="text-[#8b7327]" size={26} /></div>
                  <div>
                    <h4 className="text-lg text-[#2c2c2c] tracking-widest mb-2 font-bold" style={{ fontFamily: 'Georgia, serif' }}>PHONE</h4>
                    <a href={`tel:${BUSINESS_INFO.phoneClean}`} className="text-[#d4af37] hover:text-[#8b7327] text-2xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>{BUSINESS_INFO.phone}</a>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-[#e0d9cc] p-8 mb-8">
                <h4 className="text-lg text-[#2c2c2c] tracking-widest mb-6 flex items-center gap-3 font-bold" style={{ fontFamily: 'Georgia, serif' }}>
                  <Clock className="text-[#d4af37]" size={24} />
                  BUSINESS HOURS
                </h4>
                <div className="space-y-0">
                  {BUSINESS_INFO.hours.map((item) =>
                  <div key={item.day} className={`flex justify-between py-3 border-b border-[#f0ebe0] last:border-0 ${item.day === dayNames[today] ? "bg-[#fdf8eb] -mx-4 px-4" : ""}`} data-testid={`hours-${item.day.toLowerCase()}`}>
                      <span className={item.day === dayNames[today] ? "text-[#8b7327] font-semibold" : "text-[#555]"} style={{ fontFamily: 'Georgia, serif' }}>{item.day}{item.day === dayNames[today] && <span className="ml-2 text-xs bg-[#d4af37] text-white px-2 py-0.5 rounded">TODAY</span>}</span>
                      <span className={item.closed ? "text-[#999]" : "text-[#666]"} style={{ fontFamily: 'Georgia, serif' }}>{item.time}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-4">
                <a href="https://www.facebook.com/gentsclassicbarbershop/" target="_blank" rel="noopener noreferrer" className="w-14 h-14 bg-white border border-[#e0d9cc] flex items-center justify-center text-[#666] hover:bg-[#d4af37] hover:text-white hover:border-[#d4af37] transition-all rounded" data-testid="social-facebook"><Facebook size={24} /></a>
                <a href="https://www.instagram.com/gentsclassicbarbershop/" target="_blank" rel="noopener noreferrer" className="w-14 h-14 bg-white border border-[#e0d9cc] flex items-center justify-center text-[#666] hover:bg-[#d4af37] hover:text-white hover:border-[#d4af37] transition-all rounded" data-testid="social-instagram"><Instagram size={24} /></a>
                <a href="https://www.tiktok.com/@gentsclassicbarbershop" target="_blank" rel="noopener noreferrer" className="w-14 h-14 bg-white border border-[#e0d9cc] flex items-center justify-center text-[#666] hover:bg-[#d4af37] hover:text-white hover:border-[#d4af37] transition-all rounded" data-testid="social-tiktok">
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="w-6 h-6 fill-current">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.26V2h-3.2v13.03a2.9 2.9 0 1 1-2-2.76V9.01a6.1 6.1 0 1 0 5.2 6.02V8.36a8.08 8.08 0 0 0 4.77 1.55V6.69z" />
                  </svg>
                </a>
              </div>
            </div>

            <div>
              <div className="bg-white border border-[#e0d9cc] p-8 mb-8">
                <h3 className="text-2xl text-[#2c2c2c] mb-6" style={{ fontFamily: 'Georgia, serif' }}>Send a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label className="block text-[#555] text-sm mb-2 tracking-widest font-bold" style={{ fontFamily: 'Georgia, serif' }}>YOUR NAME</label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-[#faf9f6] border border-[#e0d9cc] px-4 py-3 text-[#2c2c2c] focus:border-[#d4af37] focus:outline-none transition-colors rounded" style={{ fontFamily: 'Georgia, serif' }} placeholder="John Smith" required data-testid="form-name" />
                  </div>
                  <div>
                    <label className="block text-[#555] text-sm mb-2 tracking-widest font-bold" style={{ fontFamily: 'Georgia, serif' }}>PHONE</label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full bg-[#faf9f6] border border-[#e0d9cc] px-4 py-3 text-[#2c2c2c] focus:border-[#d4af37] focus:outline-none transition-colors rounded" style={{ fontFamily: 'Georgia, serif' }} placeholder="+1 (262) 000-0000" required data-testid="form-phone" />
                  </div>
                  <div>
                    <label className="block text-[#555] text-sm mb-2 tracking-widest font-bold" style={{ fontFamily: 'Georgia, serif' }}>MESSAGE</label>
                    <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className="w-full bg-[#faf9f6] border border-[#e0d9cc] px-4 py-3 text-[#2c2c2c] focus:border-[#d4af37] focus:outline-none transition-colors min-h-[120px] resize-none rounded" style={{ fontFamily: 'Georgia, serif' }} placeholder="How can we help you?" required data-testid="form-message" />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-[#d4af37] hover:bg-[#b8962e] text-white text-lg tracking-widest py-6 rounded font-bold" style={{ fontFamily: 'Georgia, serif' }} data-testid="form-submit">{isSubmitting ? "SENDING..." : "SEND MESSAGE"}</Button>
                </form>
              </div>

              <div className="border border-[#e0d9cc] overflow-hidden rounded" data-testid="google-map">
                <div className="h-[350px]">
                  <iframe src={GOOGLE_MAPS_EMBED_URL} width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Location" />
                </div>
                <div className="px-6 py-4 border-t border-[#e0d9cc] bg-[#faf9f6]">
                  <a href={GOOGLE_MAPS_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#8b7327] hover:text-[#d4af37] font-semibold tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
                    <Star size={16} />
                    READ GOOGLE REVIEWS
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>);

};

// Scroll to top
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {window.scrollTo(0, 0);}, [pathname]);
  return null;
};

function App() {
  const [services, setServices] = useState(DEFAULT_SERVICES);
  const [galleryImages, setGalleryImages] = useState(DEFAULT_GALLERY_IMAGES);
  const [isSiteContentLoading, setIsSiteContentLoading] = useState(true);

  const loadSiteContent = useCallback(async () => {
    setServices(DEFAULT_SERVICES);
    setGalleryImages(DEFAULT_GALLERY_IMAGES);
    setIsSiteContentLoading(false);
  }, []);

  useEffect(() => {
    loadSiteContent();
  }, [loadSiteContent]);

  const handleSaveServices = async (nextServices) => {
    const payload = nextServices.map((service) => ({
      ...service,
      price: service.price.trim() || "$0",
      description: service.description ?? "",
      duration: service.duration ?? "",
      popular: Boolean(service.popular),
    }));
    const response = await axios.put(`${API}/admin/services`, payload);
    setServices(response.data);
    return response.data;
  };

  const handleAddGalleryImage = async (image) => {
    const payload = {
      url: image.url.trim(),
      category: image.category.trim(),
    };
    const response = await axios.post(`${API}/admin/gallery-images`, payload);
    setGalleryImages((prev) => [...prev, response.data]);
    return response.data;
  };

  const handleDeleteGalleryImage = async (imageId) => {
    await axios.delete(`${API}/admin/gallery-images/${imageId}`);
    setGalleryImages((prev) => prev.filter((image) => image.id !== imageId));
  };

  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <ScrollToTop />
        <Navbar />
        <AnimatePresence mode="wait">
          <Routes>
            <Route path="/" element={<HomePage services={services} />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/services" element={<ServicesPage services={services} />} />
            <Route path="/gallery" element={<GalleryPage galleryImages={galleryImages} />} />
            <Route path="/reviews" element={<ReviewsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route
              path="/admin"
              element={
                <AdminPage
                  services={services}
                  galleryImages={galleryImages}
                  isContentLoading={isSiteContentLoading}
                  onSaveServices={handleSaveServices}
                  onAddGalleryImage={handleAddGalleryImage}
                  onDeleteGalleryImage={handleDeleteGalleryImage} />

              } />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnimatePresence>
        <Footer />
        <Toaster position="bottom-right" theme="light" />
      </BrowserRouter>
    </div>);

}

export default App;